import { useTranslation } from 'react-i18next';

import { RunValidationStrip } from '~/features/catalog-operations/components/batch-detail/run-validation-strip';
import { useBatchHeader } from '~/features/catalog-operations/hooks/use-batch-detail';

import { ExportBrowseView } from '../components/export-browse-view';
import type { ExportConnector } from '../types/export.types';

interface ExportBrowsePageProps {
  sourceWorkflowId: string;
  connector?: ExportConnector;
  /** Run label from the link that opened this page; absent on a shared URL. */
  label?: string;
}

/**
 * Page shell for the full-run export view, with the batch's validation strip above it.
 *
 * Height-bounded on purpose: the table is rendered with `fillHeight`, so it
 * needs a constrained flex parent to grow into — the header stays put and the
 * rows scroll, rather than the whole page scrolling and taking the paginator
 * off-screen with it.
 */
export function ExportBrowsePage({ sourceWorkflowId, connector, label }: ExportBrowsePageProps) {
  const { t } = useTranslation('exports');
  // The batch behind this run, for its validation strip: a batch's id mirrors its workflow id,
  // so the export's source id finds it (an experiment's enrichment run has no header: no strip).
  const { data: batchHeader } = useBatchHeader(sourceWorkflowId);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-6">
      <header className="shrink-0">
        <h1 className="text-foreground text-lg font-semibold">{t('browse.title')}</h1>
        <p className="text-muted-foreground text-sm">
          {label
            ? t('browse.subtitleWithLabel', { name: label })
            : t('browse.subtitle', { id: sourceWorkflowId })}
        </p>
      </header>
      {/* Same validation strip as batch detail: fix structure before exporting it. */}
      <RunValidationStrip target={batchHeader} className="shrink-0" />
      <ExportBrowseView
        sourceWorkflowId={sourceWorkflowId}
        {...(connector ? { connector } : {})}
        label={label ?? sourceWorkflowId}
      />
    </div>
  );
}
